import React, { useContext, useEffect, useState } from 'react'
import API from '../../../../services/API'
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material'
import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import NumbersIcon from '@mui/icons-material/Numbers'
import SensorsIcon from '@mui/icons-material/Sensors'
import { SiteModeContext } from '../../../../components/mainFrame/MainFrame'

const priorityStyles = {
  Critical: { color: '#85276A', bg: '#FCE7F3', glow: 'rgba(133,39,106,0.22)' },
  High: { color: '#E32525', bg: '#FEE2E2', glow: 'rgba(227,37,37,0.22)' },
  Moderate: { color: '#F28B1F', bg: '#FFEDD5', glow: 'rgba(242,139,31,0.22)' },
  Low: { color: '#00964C', bg: '#DCFCE7', glow: 'rgba(0,150,76,0.18)' },
}

const Incident = ({ incident }) => {
  const { setSiteMode, setSelectedControlRoom } = useContext(SiteModeContext)
  const style = priorityStyles[incident.priority] || priorityStyles.Low
  const displayIncidentNumber = String(incident.incidentNumber)

  return (
    <Card
      elevation={0}
      onClick={() => {
        setSelectedControlRoom(incident.controlRoom._id)
        setSiteMode('eventControl')
      }}
      sx={{
        borderRadius: 4,
        border: '1px solid #E2E8F0',
        cursor: 'pointer',
        overflow: 'hidden',
        position: 'relative',
        boxShadow: '0 10px 28px rgba(36,47,64,0.08)',
        transition: 'all 0.22s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 18px 42px ${style.glow}`,
          borderColor: style.color,
        },
        '&:before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 6,
          backgroundColor: style.color,
        },
      }}
    >
      <CardContent sx={{ p: 2.5, pl: 3 }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1}
          >
            <Stack spacing={0.5}>
              <Typography fontWeight={900} color="#242F40">
                {incident.description}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {incident?.controlRoom?.linkedEvent?.title ||
                  incident?.controlRoom?.customTitle ||
                  'Control Room'}
              </Typography>
            </Stack>

            <Chip
              label={incident.priority}
              icon={
                incident.priority === 'Critical' ? (
                  <ReportProblemIcon />
                ) : undefined
              }
              size="small"
              sx={{
                borderRadius: 999,
                backgroundColor: style.bg,
                color: style.color,
                fontWeight: 900,
              }}
            />
          </Stack>

          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1} alignItems="center">
                <SensorsIcon sx={{ fontSize: 18, color: '#64748B' }} />
                <Typography variant="body2" color="text.secondary">
                  Status: <strong>{incident.incidentStatus}</strong>
                </Typography>
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1} alignItems="center">
                <ReportProblemIcon sx={{ fontSize: 18, color: '#64748B' }} />
                <Typography variant="body2" color="text.secondary">
                  Type: <strong>{incident.incidentType}</strong>
                </Typography>
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1} alignItems="center">
                <NumbersIcon sx={{ fontSize: 18, color: '#64748B' }} />
                <Typography variant="body2" color="text.secondary">
                  Incident: <strong>{displayIncidentNumber}</strong>
                </Typography>
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1} alignItems="center">
                <LocationOnIcon sx={{ fontSize: 18, color: '#64748B' }} />
                <Typography variant="body2" color="text.secondary">
                  Location:{' '}
                  <strong>{incident.location || 'Not recorded'}</strong>
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </CardContent>
    </Card>
  )
}

const OpenIncidents = () => {
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [incidents, setIncidents] = useState([])

  useEffect(() => {
    API.get('/controlroominfo/dashboard/activeincidents')
      .then((res) => {
        if (res.status === 200) setIncidents(res.data)
        else setFetchError(true)
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [])

  return (
    <Stack gap={2}>
      <Stack>
        <Typography variant="h6" fontWeight={900} color="#242F40">
          Open Incidents
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Active incidents requiring monitoring or operational action.
        </Typography>
      </Stack>

      {loading && <LinearProgress />}
      {fetchError && (
        <Alert severity="error" sx={{ borderRadius: 3 }}>
          Sorry, we're unable to load active incidents right now.
        </Alert>
      )}
      {!loading && !fetchError && incidents.length === 0 && (
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          There are no active incidents currently.
        </Alert>
      )}

      <Stack spacing={2}>
        {incidents.map((x) => (
          <Incident key={x._id} incident={x} />
        ))}
      </Stack>
    </Stack>
  )
}

export default OpenIncidents
